import React, { useState, useEffect } from 'react';
import { ArrowLeft, Sparkles, Hash, Phone, Car, Home, Heart, User, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';
import RelatedTools from '../components/RelatedTools';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import { safeJsonLd } from '../lib/sanitize';
import { useLang } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'life_path', icon: User, label_hi: 'जन्म संख्या', label_en: 'Life Path' },
  { id: 'name', icon: Hash, label_hi: 'नाम अंक', label_en: 'Name Number' },
  { id: 'mobile', icon: Phone, label_hi: 'मोबाइल नंबर', label_en: 'Mobile' },
  { id: 'vehicle', icon: Car, label_hi: 'वाहन नंबर', label_en: 'Vehicle' },
  { id: 'house', icon: Home, label_hi: 'घर का नंबर', label_en: 'House' },
  { id: 'compatibility', icon: Heart, label_hi: 'अनुकूलता', label_en: 'Compatibility' },
];

function ResultCard({ result, lang }) {
  if (!result) return null;
  
  const isLucky = result.is_lucky;
  
  return (
    <motion.div className="mt-8 space-y-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Main Number */}
      {(result.life_path || result.destiny_number || result.number_value) && (
        <div className="glass-card p-6 text-center">
          <p className="text-sm text-text-muted mb-2">{lang === 'hi' ? 'आपकी मूल संख्या' : 'Your Core Number'}</p>
          <div className="text-6xl font-heading text-gold-gradient font-bold mb-2">
            {result.life_path || result.destiny_number || result.number_value}
          </div>
          {result.ruling_planet && (
            <p className="text-primary text-sm">{lang === 'hi' ? 'स्वामी ग्रह' : 'Ruling Planet'}: <span className="text-gold font-medium">{result.ruling_planet}</span></p>
          )}
          {isLucky !== undefined && (
            <div className={`inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full text-sm font-medium ${isLucky ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-orange-500/15 text-orange-400 border border-orange-500/30'}`}>
              {isLucky ? (lang === 'hi' ? '✓ शुभ संख्या' : '✓ Lucky Number') : (lang === 'hi' ? '⚠ सुधार आवश्यक' : '⚠ Needs Correction')}
            </div>
          )}
        </div>
      )}

      {/* Traits */}
      {result.traits && (
        <div className="glass-card p-5">
          <p className="text-xs text-text-muted uppercase tracking-wider mb-2">{lang === 'hi' ? 'गुण' : 'Traits'}</p>
          <p className="text-text-main font-medium">{result.traits}</p>
        </div>
      )}

      {/* Extra Numbers */}
      {result.soul_number && (
        <div className="grid grid-cols-3 gap-3">
          <div className="glass-card p-4 text-center">
            <p className="text-2xl font-heading text-gold">{result.destiny_number}</p>
            <p className="text-[10px] text-text-muted mt-1">{lang === 'hi' ? 'भाग्य अंक' : 'Destiny'}</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-2xl font-heading text-primary">{result.soul_number}</p>
            <p className="text-[10px] text-text-muted mt-1">{lang === 'hi' ? 'आत्मा अंक' : 'Soul'}</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-2xl font-heading text-purple-400">{result.personality_number}</p>
            <p className="text-[10px] text-text-muted mt-1">{lang === 'hi' ? 'व्यक्तित्व' : 'Personality'}</p>
          </div>
        </div>
      )}

      {/* Lucky Info */}
      {result.lucky_color && (
        <div className="flex gap-3 flex-wrap">
          <div className="glass-card px-4 py-2 flex items-center gap-2">
            <span className="text-xs text-text-muted">{lang === 'hi' ? 'शुभ रंग' : 'Lucky Color'}:</span>
            <span className="text-gold text-sm font-medium">{result.lucky_color}</span>
          </div>
          {result.lucky_day && (
            <div className="glass-card px-4 py-2 flex items-center gap-2">
              <span className="text-xs text-text-muted">{lang === 'hi' ? 'शुभ दिन' : 'Lucky Day'}:</span>
              <span className="text-gold text-sm font-medium">{result.lucky_day}</span>
            </div>
          )}
          {result.lucky_numbers && (
            <div className="glass-card px-4 py-2 flex items-center gap-2">
              <span className="text-xs text-text-muted">{lang === 'hi' ? 'शुभ अंक' : 'Lucky Nos'}:</span>
              <span className="text-gold text-sm font-medium">{result.lucky_numbers?.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {/* Compatibility */}
      {result.compatibility_score && (
        <div className="glass-card p-6 text-center">
          <p className="text-sm text-text-muted mb-2">{lang === 'hi' ? 'अनुकूलता स्कोर' : 'Compatibility Score'}</p>
          <div className={`text-5xl font-heading font-bold ${result.compatibility_score > 70 ? 'text-green-400' : result.compatibility_score > 50 ? 'text-gold' : 'text-orange-400'}`}>
            {result.compatibility_score}%
          </div>
          <div className="flex justify-center gap-8 mt-4 text-sm">
            <div><span className="text-text-muted">{result.person1?.name}:</span> <span className="text-gold font-bold">{result.person1?.number}</span></div>
            <div><span className="text-text-muted">{result.person2?.name}:</span> <span className="text-gold font-bold">{result.person2?.number}</span></div>
          </div>
        </div>
      )}

      {/* AI Interpretation */}
      {result.interpretation && (
        <div className="bg-gold/5 border border-gold/20 rounded-xl p-5">
          <div className="flex items-center gap-2 text-gold mb-3">
            <Sparkles className="w-4 h-4" />
            <span className="font-heading text-sm">{lang === 'hi' ? 'विवेक मुद्गल की विशेष व्याख्या' : 'Expert Analysis by Vivek Mudgal'}</span>
          </div>
          <p className="text-text-muted leading-relaxed whitespace-pre-line">{result.interpretation}</p>
        </div>
      )}

      {/* CTA */}
      <div className="text-center pt-4">
        <a href="/#contact" className="btn-gold inline-flex items-center gap-2" data-testid="numerology-consult-cta">
          {lang === 'hi' ? 'विस्तृत रिपोर्ट के लिए परामर्श लें' : 'Get Detailed Report - Consult Now'}
        </a>
      </div>
    </motion.div>
  );
}

export default function NumerologyPage() {
  const [activeTab, setActiveTab] = useState('life_path');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', dob: '', number: '', name1: '', name2: '', dob1: '', dob2: '' });
  const [userInfo, setUserInfo] = useState({ fullName: '', mobile: '', email: '', dob: '' });
  const [isRegistered, setIsRegistered] = useState(false);
  const { lang } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check if already registered
    const saved = localStorage.getItem('numerology_user');
    if (saved) { setIsRegistered(true); setUserInfo(JSON.parse(saved)); }
  }, [lang]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!userInfo.fullName || !userInfo.mobile || !userInfo.email) return;
    // Save lead to backend
    try {
      await axios.post(`${API}/api/numerology/register`, userInfo);
    } catch (err) { /* continue anyway */ }
    localStorage.setItem('numerology_user', JSON.stringify(userInfo));
    setIsRegistered(true);
    // Pre-fill form with user info
    setForm(prev => ({ ...prev, name: userInfo.fullName, dob: userInfo.dob }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = { type: activeTab, language: lang, user_mobile: userInfo.mobile };
      if (activeTab === 'life_path') { payload.dob = form.dob || userInfo.dob; payload.name = form.name || userInfo.fullName; }
      else if (activeTab === 'name') { payload.name = form.name || userInfo.fullName; }
      else if (activeTab === 'mobile') { payload.number = form.number || userInfo.mobile; }
      else if (activeTab === 'vehicle') { payload.number = form.number; }
      else if (activeTab === 'house') { payload.number = form.number; }
      else if (activeTab === 'compatibility') { payload.name1 = form.name1; payload.name2 = form.name2; payload.dob1 = form.dob1; payload.dob2 = form.dob2; }
      
      const res = await axios.post(`${API}/api/numerology/calculate`, payload, { timeout: 60000 });
      setResult(res.data);
    } catch (err) {
      console.error('Numerology error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Free Numerology Calculator | अंकशास्त्र | Lucky Number Analysis"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'अंकशास्त्र', path: '/numerology' },
        ]}
        description="Free Vedic Numerology calculator — find your Mulank (Driver Number), Bhagyank (Destiny Number), Lucky Mobile Number, Vehicle Number, House Number, and Name Numerology. Detailed Numerology report by Vivek Mudgal Astrologer (33+ years experience)."
        path="/numerology"
        keywords="numerology, अंकशास्त्र, mulank, bhagyank, lucky number, name numerology, mobile number numerology, vehicle number, house number, Vivek Mudgal"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Free Numerology Calculator',
          description: 'Vedic Numerology analysis — Mulank, Bhagyank, Name & Number compatibility',
          provider: { '@type': 'Person', name: 'Vivek Mudgal' },
          serviceType: 'Numerology Consultation',
          areaServed: 'India',
        }}
      />
      <Header />
      <FloatingActions />

      {/* Hero */}
      <section className="pt-32 pb-8 cosmic-bg">
        <div className="container-custom text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:gap-3 transition-all mb-6 text-sm" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" /> {lang === 'hi' ? 'होम' : 'Home'}
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-4">
            {lang === 'hi' ? 'वैदिक अंकशास्त्र' : 'VEDIC NUMEROLOGY'}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main mb-4">
            {lang === 'hi' ? 'अंकशास्त्र Calculator' : 'Numerology Calculator'}
          </h1>
          <p className="text-text-muted max-w-xl mx-auto">
            {lang === 'hi'
              ? 'नाम, जन्मतिथि, मोबाइल, वाहन, घर — सबका अंक जानें, शुभ-अशुभ पहचानें'
              : 'Know your numbers — Name, DOB, Mobile, Vehicle, House — Check if they are Lucky'}
          </p>
          <div className="divine-divider mt-6 max-w-xs mx-auto" />
        </div>
      </section>

      {/* Calculator */}
      <section className="section-padding">
        <div className="container-custom max-w-2xl">
          
          {/* Registration Gate */}
          {!isRegistered ? (
            <motion.div className="glass-card p-6 sm:p-8" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="numerology-register-form">
              <div className="text-center mb-6">
                <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
                <h2 className="font-heading text-2xl text-text-main">
                  {lang === 'hi' ? 'अपनी जानकारी दें — Free Reading पाएं' : 'Enter Your Details — Get Free Reading'}
                </h2>
                <p className="text-text-muted text-sm mt-2">
                  {lang === 'hi' ? 'एक बार भरें, सभी calculators unlimited use करें' : 'Fill once, use all calculators unlimited'}
                </p>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'पूरा नाम *' : 'Full Name *'}</label>
                  <input type="text" value={userInfo.fullName} onChange={(e) => setUserInfo({ ...userInfo, fullName: e.target.value })} 
                    placeholder={lang === 'hi' ? 'अपना नाम लिखें' : 'Enter your name'} required className="w-full" data-testid="reg-name" />
                </div>
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'मोबाइल नंबर *' : 'Mobile Number *'}</label>
                  <input type="tel" value={userInfo.mobile} onChange={(e) => setUserInfo({ ...userInfo, mobile: e.target.value })} 
                    placeholder="e.g. 9876543210" required className="w-full" data-testid="reg-mobile" />
                </div>
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'ईमेल *' : 'Email *'}</label>
                  <input type="email" value={userInfo.email} onChange={(e) => setUserInfo({ ...userInfo, email: e.target.value })} 
                    placeholder="email@example.com" required className="w-full" data-testid="reg-email" />
                </div>
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'जन्मतिथि *' : 'Date of Birth *'}</label>
                  <input type="date" value={userInfo.dob} onChange={(e) => setUserInfo({ ...userInfo, dob: e.target.value })} 
                    required className="w-full" data-testid="reg-dob" />
                </div>
                <button type="submit" className="btn-gold w-full text-base flex items-center justify-center gap-2" data-testid="reg-submit-btn">
                  <Calculator className="w-5 h-5" /> {lang === 'hi' ? 'Free Reading शुरू करें' : 'Start Free Reading'}
                </button>
                <p className="text-[10px] text-text-muted text-center">
                  {lang === 'hi' ? 'आपकी जानकारी सुरक्षित है। हम सिर्फ आपकी reading के लिए use करेंगे।' : 'Your information is safe. We only use it for your reading.'}
                </p>
              </form>
            </motion.div>
          ) : (
            <>
          {/* Tabs */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-8" data-testid="numerology-tabs">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setResult(null); }}
                className={`flex flex-col items-center gap-1 p-3 rounded-xl text-center transition-all ${activeTab === tab.id ? 'bg-gold/15 border border-gold/30 text-gold' : 'bg-white/5 border border-white/10 text-text-muted hover:border-gold/20'}`}
                data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-5 h-5" />
                <span className="text-[10px] sm:text-xs font-medium">{lang === 'hi' ? tab.label_hi : tab.label_en}</span>
              </button>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="glass-card p-6 space-y-4" data-testid="numerology-form">
            {activeTab === 'life_path' && (
              <>
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'पूरा नाम' : 'Full Name'}</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === 'hi' ? 'अपना नाम लिखें' : 'Enter your name'} required className="w-full" data-testid="input-name" />
                </div>
                <div>
                  <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'जन्मतिथि' : 'Date of Birth'}</label>
                  <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} required className="w-full" data-testid="input-dob" />
                </div>
              </>
            )}
            {activeTab === 'name' && (
              <div>
                <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'पूरा नाम (English में)' : 'Full Name (in English)'}</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Vivek Mudgal" required className="w-full" data-testid="input-name" />
              </div>
            )}
            {activeTab === 'mobile' && (
              <div>
                <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'}</label>
                <input type="tel" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="e.g. 9311369729" required className="w-full" data-testid="input-mobile" />
              </div>
            )}
            {activeTab === 'vehicle' && (
              <div>
                <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'वाहन नंबर' : 'Vehicle Number'}</label>
                <input type="text" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="e.g. HR26DK8899" required className="w-full" data-testid="input-vehicle" />
              </div>
            )}
            {activeTab === 'house' && (
              <div>
                <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'घर/फ्लैट नंबर' : 'House/Flat Number'}</label>
                <input type="text" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="e.g. 302 या B-15" required className="w-full" data-testid="input-house" />
              </div>
            )}
            {activeTab === 'compatibility' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'पहला नाम' : 'Person 1 Name'}</label>
                    <input type="text" value={form.name1} onChange={(e) => setForm({ ...form, name1: e.target.value })} placeholder="Name 1" required className="w-full" data-testid="input-name1" />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">{lang === 'hi' ? 'दूसरा नाम' : 'Person 2 Name'}</label>
                    <input type="text" value={form.name2} onChange={(e) => setForm({ ...form, name2: e.target.value })} placeholder="Name 2" required className="w-full" data-testid="input-name2" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-text-muted mb-1">DOB 1</label>
                    <input type="date" value={form.dob1} onChange={(e) => setForm({ ...form, dob1: e.target.value })} className="w-full" data-testid="input-dob1" />
                  </div>
                  <div>
                    <label className="block text-sm text-text-muted mb-1">DOB 2</label>
                    <input type="date" value={form.dob2} onChange={(e) => setForm({ ...form, dob2: e.target.value })} className="w-full" data-testid="input-dob2" />
                  </div>
                </div>
              </>
            )}

            <button type="submit" disabled={loading} className="btn-gold w-full flex items-center justify-center gap-2 text-base" data-testid="calculate-btn">
              {loading ? (
                <><span className="animate-spin">🔮</span> {lang === 'hi' ? 'गणना हो रही है...' : 'Calculating...'}</>
              ) : (
                <><Calculator className="w-5 h-5" /> {lang === 'hi' ? 'गणना करें' : 'Calculate'}</>
              )}
            </button>
          </form>

          {/* Result */}
          <ResultCard result={result} lang={lang} />
            </>
          )}
        </div>
      </section>

      {/* SEO Schema */}
      <script type="application/ld+json" {...safeJsonLd({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Numerology Calculator | अंकशास्त्र Calculator",
        "description": "Free Vedic Numerology Calculator - Life Path, Name, Mobile, Vehicle, House number analysis with AI interpretation.",
        "url": "https://viratastro.cloud/numerology",
        "author": {"@type": "Person", "name": "Vivek Mudgal Astrologer"},
        "applicationCategory": "LifestyleApplication"
      })} />

      <RelatedTools />
      <Footer />
    </div>
  );
}
